import React, { useEffect, useMemo, useState } from 'react';
import useCoinGecko from '../hooks/useCoinGecko';
import { CoinGeckoGlobalData, CoinGeckoMarket, CoinGeckoResourceState } from '../types';

interface FearGreedData {
  value: string;
  value_classification: string;
}

const MAJOR_IDS = ['bitcoin', 'ethereum', 'solana'];

function formatPrice(value: number): string {
  if (value < 1) {
    return `$${value.toFixed(6)}`;
  }
  return `$${value.toLocaleString()}`;
}

function renderSparkline(prices: number[], color: string) {
  if (!prices || prices.length < 2) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const width = 60;
  const height = 30;
  const step = width / (prices.length - 1);
  const path = prices
    .map((price, index) => {
      const x = index * step;
      const y = height - ((price - min) / range) * height;
      return `${index === 0 ? 'M' : 'L'} ${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" className="overflow-visible">
      <path d={path} stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CoinRow: React.FC<{ coin: CoinGeckoMarket }> = ({ coin }) => {
  const isPositive = coin.priceChangePercentage24h >= 0;
  const color = isPositive ? '#10B981' : '#EF4444';

  return (
    <div className="flex items-center justify-between rounded-xl bg-white/50 p-3 transition-colors hover:bg-white/70">
      <div className="flex items-center space-x-3">
        <img src={coin.image} alt={coin.name} className="h-8 w-8 rounded-full" />
        <div>
          <div className="w-16 truncate text-sm font-bold uppercase text-gray-700" title={coin.name}>{coin.symbol}</div>
          <div className="font-mono text-xs text-gray-500">{formatPrice(coin.currentPrice)}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-end">
          {renderSparkline(coin.sparklineIn7d, color)}
          <div className={`mt-1 text-xs font-bold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
            {isPositive ? '+' : ''}{coin.priceChangePercentage24h.toFixed(2)}%
          </div>
        </div>
      </div>
    </div>
  );
};

const CryptoTicker: React.FC = () => {
  const { ensureMarkets, ensureResource, getMarketById, resources } = useCoinGecko();
  const [activeTab, setActiveTab] = useState<'major' | 'trending' | 'global' | 'events'>('major');
  const [fearGreed, setFearGreed] = useState<FearGreedData | null>(null);
  const [fearGreedError, setFearGreedError] = useState(false);

  useEffect(() => {
    void ensureMarkets(MAJOR_IDS, { track: false });
  }, [ensureMarkets]);

  useEffect(() => {
    if (activeTab === 'trending') {
      void ensureResource('trending');
    }
    if (activeTab === 'global') {
      void ensureResource('global');
    }
  }, [activeTab, ensureResource]);

  useEffect(() => {
    let cancelled = false;
    const fetchFearGreed = async () => {
      try {
        const response = await fetch('https://api.alternative.me/fng/');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setFearGreed(data.data?.[0] ?? null);
          setFearGreedError(false);
        }
      } catch (error) {
        console.error('Failed to fetch Fear & Greed:', error);
        if (!cancelled) {
          setFearGreedError(true);
        }
      }
    };

    void fetchFearGreed();
    return () => {
      cancelled = true;
    };
  }, []);

  const majorCoins = useMemo(() => {
    return MAJOR_IDS.map((id) => getMarketById(id)).filter(Boolean) as CoinGeckoMarket[];
  }, [getMarketById]);

  const trendingState = resources.trending as CoinGeckoResourceState<string[]> | undefined;
  const trendingCoins = useMemo(() => {
    return (trendingState?.data ?? [])
      .map((id) => getMarketById(id))
      .filter(Boolean) as CoinGeckoMarket[];
  }, [getMarketById, trendingState?.data]);

  const globalState = resources.global as CoinGeckoResourceState<CoinGeckoGlobalData> | undefined;
  const displayedCoins = activeTab === 'major' ? majorCoins : trendingCoins;

  const isMarketLoading = activeTab === 'major'
    ? majorCoins.length === 0
    : activeTab === 'trending' && trendingState?.status === 'loading' && trendingCoins.length === 0;

  const getFearGreedColor = (value: number) => {
    if (value >= 75) return 'text-green-600';
    if (value >= 50) return 'text-green-500';
    if (value >= 25) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="space-y-3">
      {fearGreed && (
        <div className="flex items-center justify-between rounded-xl bg-white/50 p-3">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Fear &amp; Greed</span>
            <span className={`text-sm font-extrabold ${getFearGreedColor(Number(fearGreed.value))}`}>
              {fearGreed.value_classification}
            </span>
          </div>
          <div className={`text-2xl font-black ${getFearGreedColor(Number(fearGreed.value))}`}>
            {fearGreed.value}
          </div>
        </div>
      )}

      {!fearGreed && fearGreedError && (
        <div className="rounded-xl bg-orange-50 p-3 text-xs text-orange-700">
          Fear &amp; Greed feed is temporarily unavailable.
        </div>
      )}

      <div className="flex overflow-x-auto rounded-lg bg-white/30 p-1">
        {[
          { id: 'major', label: 'Major' },
          { id: 'trending', label: 'Trending' },
          { id: 'global', label: 'Global' },
          { id: 'events', label: 'Events' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`flex-1 whitespace-nowrap rounded-md px-1 py-1 text-[10px] font-bold transition-all md:text-xs ${
              activeTab === tab.id ? 'bg-white text-fuchsia-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'events' ? (
        <div className="space-y-3 p-1">
          <div className="rounded-xl bg-white/50 p-3">
            <span className="mb-1 block text-xs font-bold text-gray-600">Next BTC Halving (Est.)</span>
            <div className="text-xl font-black text-amber-600">
              {Math.ceil((new Date('2028-04-17').getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} Days
            </div>
            <span className="text-[10px] text-gray-400">Target: April 2028</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <a href="https://coinmarketcal.com/" target="_blank" rel="noreferrer" className="rounded-lg bg-white/50 p-2 text-center transition-colors hover:bg-white/80">
              <div className="text-[10px] font-bold text-gray-600">CoinMarketCal</div>
            </a>
            <a href="https://token.unlocks.app/" target="_blank" rel="noreferrer" className="rounded-lg bg-white/50 p-2 text-center transition-colors hover:bg-white/80">
              <div className="text-[10px] font-bold text-gray-600">Token Unlocks</div>
            </a>
            <a href="https://coindar.org/" target="_blank" rel="noreferrer" className="rounded-lg bg-white/50 p-2 text-center transition-colors hover:bg-white/80">
              <div className="text-[10px] font-bold text-gray-600">Coindar</div>
            </a>
            <a href="https://www.forexfactory.com/calendar" target="_blank" rel="noreferrer" className="rounded-lg bg-white/50 p-2 text-center transition-colors hover:bg-white/80">
              <div className="text-[10px] font-bold text-gray-600">Macro/Forex</div>
            </a>
          </div>
        </div>
      ) : activeTab === 'global' ? (
        globalState?.data ? (
          <div className="space-y-3 p-1">
            <div className="flex items-center justify-between rounded-xl bg-white/50 p-3">
              <span className="text-xs font-bold text-gray-600">Total Market Cap</span>
              <span className="text-sm font-bold text-gray-800">${(globalState.data.total_market_cap.usd / 1e12).toFixed(2)}T</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-white/50 p-3">
              <span className="text-xs font-bold text-gray-600">24h Volume</span>
              <span className="text-sm font-bold text-gray-800">${(globalState.data.total_volume.usd / 1e9).toFixed(2)}B</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-white/50 p-3">
              <span className="text-xs font-bold text-amber-600">BTC Dominance</span>
              <span className="text-sm font-bold text-amber-600">{(globalState.data.market_cap_percentage.btc ?? 0).toFixed(1)}%</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-white/50 p-3">
              <span className="text-xs font-bold text-gray-600">ETH Dominance</span>
              <span className="text-sm font-bold text-indigo-600">{(globalState.data.market_cap_percentage.eth ?? 0).toFixed(1)}%</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-white/50 p-3">
              <span className="text-xs font-bold text-gray-600">Active Cryptos</span>
              <span className="text-sm font-bold text-gray-800">{globalState.data.active_cryptocurrencies.toLocaleString()}</span>
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-white/50 p-4 text-xs text-gray-500">
            {globalState?.status === 'loading' ? 'Loading global metrics...' : 'Global metrics will load on demand.'}
          </div>
        )
      ) : isMarketLoading ? (
        <div className="animate-pulse p-4 text-center text-gray-500">Loading market data...</div>
      ) : (
        <div className="space-y-2">
          {displayedCoins.map((coin) => (
            <CoinRow key={coin.id} coin={coin} />
          ))}
        </div>
      )}

      <div className="mt-2 flex justify-center gap-4 text-center">
        <a href="https://www.coingecko.com/" target="_blank" rel="noreferrer" className="text-[10px] text-gray-400 hover:text-gray-600">
          Data by CoinGecko
        </a>
        <a href="https://alternative.me/crypto/fear-and-greed-index/" target="_blank" rel="noreferrer" className="text-[10px] text-gray-400 hover:text-gray-600">
          Index by Alternative.me
        </a>
      </div>
    </div>
  );
};

export default CryptoTicker;
